import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Row as BootstrapRow,
  Collapse,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Form from './form/Form';
import { useParams } from 'react-router-dom';
import Toolbox from './toolbox/Toolbox';
import useForm from '../../../../hooks/api/form/useForm';
import Rules from './rules/Rules';
import FormSettingsComponent from './formSettings/FormSettingsComponent';
import {
  addLocalization,
  removeLocalization,
  setForm,
  setLanguageTouched,
  setLocalizations,
  setSelectedLanguage,
  showFields,
  updateDiscountCodes,
  updatePaymentModes,
} from '../../../../slices/formadmin';
import { RootState } from '../../../../store';
import useUpdateForm from '../../../../hooks/api/form/useUpdateForm';
import { useIntl } from 'react-intl';
import LoadingSpinner from '../../../../components/layout/loadingSpinner/LoadingSpinner';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../../../models/security/security';
import { addErrorToast } from '../../../../components/errors/toast/ToastUtils';
import usePaymentAccounts from '../../../../hooks/api/payment/stripe/usePaymentAccounts';
import CultureText from '../../../../components/culture/CultureText';
import { languages } from '../../../../utilities/CultureUtilities';
import _ from 'lodash';
import ModalComponent from '../../../../components/modal/ModalComponent';

const FormAdmin: FC = () => {
  const [settings, setSettings] = useState<boolean>(false);
  const [formSecurity, setFormSecurity] = useState<SecurityModel | null>(null);
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const { formId } = useParams<{ formId?: string | undefined }>();
  const { form, setFormId, isLoading, setOrganizationId: setId } = useForm(organizationId);
  const { updateForm, isLoading: isLoadingUpdateForm, setOrganizationId } = useUpdateForm(organizationId);
  const { paymentAccounts, setOrganizationId: setOrgId, isLoaded } = usePaymentAccounts(organizationId);
  const formAdmin = useSelector((state: RootState) => state.formAdmin.form);
  const fieldsShown = useSelector((state: RootState) => state.formAdmin.fieldsShown);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const intl = useIntl();
  const dispatch = useDispatch();
  const ref = useRef<{ getFormikRefs: () => any }>(null);
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage) as string;
  const [selectedDeletedLanguage, setShowSelectedDeleteLanguage] = useState<string | null>(null);
  const [showLanguageWarningModal, setShowLanguageWarningModal] = useState<boolean>(false);
  const languageTouched = useSelector((state: RootState) => state.formAdmin.languageTouched) as any;

  const DeleteLanguageModal = (
    <ModalComponent
      closeSettings={() => setShowSelectedDeleteLanguage(null)}
      buttons={[
        {
          text: intl.formatMessage({ id: 'cancel' }),
          buttonVariant: 'secondary',
          onClick: () => setShowSelectedDeleteLanguage(null),
        },
        {
          text: intl.formatMessage({ id: 'delete' }),
          buttonVariant: 'danger',
          onClick: () => {
            setShowSelectedDeleteLanguage(null);
            dispatch(removeLocalization(selectedDeletedLanguage));
          },
        },
      ]}
      show={selectedDeletedLanguage != null}
      modalHeading={intl.formatMessage({
        id: 'formList.deleteModal.deleteModalHeader',
      })}
      modalSize="lg"
    >
      <div className="d-flex justify-content-center flex-column">
        <label className="modal-label">
          <b>
            {intl.formatMessage({ id: 'formAdmin.deleteLanguageConfirmation' }, { language: selectedDeletedLanguage })}
          </b>
        </label>
      </div>
    </ModalComponent>
  );

  const LanguageWarningsModal = (
    <ModalComponent
      closeSettings={() => setShowLanguageWarningModal(false)}
      buttons={[
        {
          text: intl.formatMessage({ id: 'cancel' }),
          buttonVariant: 'secondary',
          onClick: () => setShowLanguageWarningModal(false),
        },
        {
          text: intl.formatMessage({ id: 'save' }),
          buttonVariant: 'primary',
          onClick: () => {
            setShowLanguageWarningModal(false);
            handleUpdateForm();
          },
        },
      ]}
      show={showLanguageWarningModal}
      modalHeading={intl.formatMessage({
        id: 'formList.deleteModal.deleteModalHeader',
      })}
      modalSize="lg"
    >
      <div className="d-flex justify-content-center flex-column">
        <label className="modal-label">
          <b>{intl.formatMessage({ id: 'formAdmin.languageWarning' })}</b>
        </label>
      </div>
    </ModalComponent>
  );

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
      setId(organizationId);
      setOrgId(organizationId);
    }
    if (formId) {
      setFormId(formId);
    }
  }, [formId, organizationId]);

  useEffect(() => {
    dispatch(setForm(form));
    dispatch(setLanguageTouched({}));

    if (!form?.localizations) {
      dispatch(setLocalizations([intl.locale]));
      dispatch(setSelectedLanguage(intl.locale));
    } else {
      dispatch(
        setSelectedLanguage(_.includes(form?.localizations, intl.locale) ? intl.locale : form?.localizations[0]),
      );
    }
  }, [form]);

  useEffect(() => {
    if (isLoaded) {
      if (paymentAccounts && paymentAccounts?.length == 0) {
        addErrorToast({ message: intl.formatMessage({ id: 'formAdmin.noPaymentAccounts' }) });
      }
    }
  }, [isLoaded]);

  useEffect(() => {
    if (security) {
      setFormSecurity(getSecuritySection(security, SecuritySections.FORM));
    }
  }, [security]);

  if (!formAdmin) {
    return null;
  }

  const handleUpdateForm = async () => {
    const { paymentModesRef, discountCodesRef } = ref.current?.getFormikRefs();

    await paymentModesRef.current?.validateForm();
    paymentModesRef.current?.handleSubmit();

    await discountCodesRef.current?.validateForm();
    discountCodesRef.current?.handleSubmit();

    if (!formAdmin.paymentEnabled || (paymentModesRef.current?.isValid && discountCodesRef.current?.isValid)) {
      const paymentModes = formAdmin.paymentEnabled
        ? { modes: paymentModesRef.current?.values.modes, applyTo: [] }
        : null;

      const discountCodes = formAdmin.paymentEnabled ? [...discountCodesRef.current?.values.discountCodes] : null;

      const form = {
        ...formAdmin,
        paymentModes,
        discountCodes,
      };

      dispatch(updatePaymentModes(paymentModes));
      dispatch(updateDiscountCodes(discountCodes));

      if (formAdmin.fixedAmount != null && formAdmin.fixedAmount != 0 && !formAdmin.paymentEnabled) {
        addErrorToast({ message: intl.formatMessage({ id: 'formAdmin.enablePaymentError' }) });
      } else if (formAdmin.paymentEnabled && isLoaded && paymentAccounts && paymentAccounts?.length == 0) {
        addErrorToast({ message: intl.formatMessage({ id: 'formAdmin.noPaymentAccounts' }) });
      } else {
        updateForm(form, null);
        dispatch(setLanguageTouched({}));
      }
    } else {
      if (!paymentModesRef.current?.isValid) {
        addErrorToast({ message: intl.formatMessage({ id: 'formAdmin.paymentModesError' }) });
      } else if (!discountCodesRef.current?.isValid) {
        addErrorToast({ message: intl.formatMessage({ id: 'formAdmin.discountCodesError' }) });
      }
    }
  };

  const shouldShowPopup = () => {
    let hasWarnings = false;

    for (const id in languageTouched) {
      const languageTouchedValue = languageTouched[id];

      if (!_.isEmpty(languageTouchedValue)) {
        for (const localization of formAdmin.localizations) {
          if (!languageTouchedValue[localization]) {
            hasWarnings = true;
          }
        }
      }
    }

    return hasWarnings;
  };

  const renderTooltip = (text) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  return (
    <div className="form-admin">
      {DeleteLanguageModal}
      {LanguageWarningsModal}
      {formAdmin?.localizations && (
        <Tabs
          activeKey={selectedLanguage}
          onSelect={(k) => {
            if (k !== null && k != 'add-language') {
              dispatch(setSelectedLanguage(k));
            } else if (k == 'add-language') {
              const language = _.find(languages, (lang) => !_.includes(formAdmin.localizations, lang.locale));
              if (language) {
                dispatch(addLocalization(language.locale));
              }
            }
          }}
          className="language-tabs"
          style={{ borderBottom: '1px solid #dee2e6', marginRight: 0, marginLeft: 0 }}
        >
          {formAdmin?.localizations.map((localization) => {
            const language = _.find(languages, { locale: localization });
            if (language) {
              return (
                <Tab
                  title={
                    <div className="d-flex align-items-center">
                      <span>{intl.formatMessage({ id: language.text })}</span>
                      {formAdmin?.localizations.length > 1 && (
                        <div
                          className="delete-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSelectedDeleteLanguage(language.locale);
                          }}
                        >
                          <span className="mdi mdi-close"></span>
                        </div>
                      )}
                    </div>
                  }
                  eventKey={language.locale}
                  key={language.locale}
                  tabClassName={'d-flex align-items-center'}
                ></Tab>
              );
            }
            return null;
          })}
          {formAdmin?.localizations.length < languages.length && (
            <Tab
              tabClassName="add-icon"
              title={
                <div>
                  <span className="mdi mdi-plus"></span>
                </div>
              }
              key="add-language"
              eventKey="add-language"
            ></Tab>
          )}
        </Tabs>
      )}
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between">
            <div>
              <div style={{ display: 'flex' }}>
                <h5 style={{ marginRight: '10px' }}>
                  <CultureText culture={selectedLanguage} text={formAdmin.name}></CultureText>
                </h5>
                {formSecurity?.update && (
                  <OverlayTrigger
                    delay={{ show: 50, hide: 100 }}
                    overlay={renderTooltip(intl.formatMessage({ id: 'formAdmin.editFormProperties' }))}
                    placement="top"
                  >
                    <span
                      style={{ fontSize: '1.25rem' }}
                      className="mdi mdi-pencil-outline modal-btn"
                      onClick={() => setSettings(!settings)}
                    ></span>
                  </OverlayTrigger>
                )}
              </div>
            </div>
            <div>
              <div style={{ textAlign: 'end', marginBottom: 16 }}>
                {form && (
                  <React.Fragment>
                    <span style={{ marginRight: 8 }}>
                      <Button
                        onClick={() => (shouldShowPopup() ? setShowLanguageWarningModal(true) : handleUpdateForm())}
                        disabled={!formSecurity?.update}
                      >
                        {!isLoadingUpdateForm && <span className="mdi mdi-content-save"></span>}
                        {isLoadingUpdateForm && <Spinner animation="border" size="sm" />}
                        {intl.formatMessage({ id: 'formAdmin.save' })}
                      </Button>
                    </span>
                    <span style={{ marginRight: 8 }}>
                      <a href={'/registrations/preview/' + formAdmin.id} target="_blank" rel="noreferrer">
                        <Button disabled={!formSecurity?.read}>
                          <span className="mdi mdi-eye"></span>
                          {intl.formatMessage({ id: 'formAdmin.preview' })}
                        </Button>
                      </a>
                    </span>
                    <span style={{ marginRight: 8 }}>
                      <Rules />
                    </span>
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
          <BootstrapRow>
            <Col xl={9}>
              {(isLoading || !formAdmin) && <LoadingSpinner />}
              {!isLoading && formAdmin && <Form ref={ref} />}
              {settings && (
                <FormSettingsComponent
                  show={settings}
                  closeSettings={() => setSettings(!settings)}
                ></FormSettingsComponent>
              )}
            </Col>
            {formSecurity?.update && (
              <Col xl={3}>
                <div id="accordion-admin">
                  <Card>
                    <Card.Header
                      className="add-fields d-flex justify-content-center fw-bold"
                      onClick={() => dispatch(showFields())}
                    >
                      {intl.formatMessage({ id: 'formAdmin.fields' })}
                    </Card.Header>
                    <Collapse in={fieldsShown}>
                      <Card.Body style={{ padding: '0px' }}>
                        <Toolbox />
                      </Card.Body>
                    </Collapse>
                  </Card>
                </div>
              </Col>
            )}
          </BootstrapRow>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FormAdmin;
